/**
 * Labs Hook
 * Lab provisioning and management
 *
 * Note: Instance status is updated automatically via EventBridge events from CloudFormation.
 * No polling is needed - just refresh to see updated status.
 */

import { useState, useCallback } from 'react';
import { apiGet, apiPost, apiDelete, ApiError } from '../services/api';
import type { StudentLab, LabInstance } from '@cloak-next/shared';

/**
 * Response from GET /api/labs/status/:className
 */
export interface LabStatusResponse {
  className: string;
  maxStudents: number;
  totalInstances: number;
  primedInstances: number;
  activeInstances: number;
  provisioningInstances: number;
  failedInstances: number;
  instances: LabInstance[];
}

/**
 * Response from POST /api/labs/prime/:className
 */
interface PrimeLabsResponse {
  success: boolean;
  message: string;
  data: {
    primedCount: number;
    classStatus: LabStatusResponse;
  };
}

/**
 * Response from DELETE /api/labs/reduce/:className
 */
interface ReduceLabsResponse {
  success: boolean;
  message: string;
  data: {
    terminatedCount: number;
    remainingUnassigned: number;
  };
}

export interface UseLabsResult {
  // Student labs
  studentLabs: StudentLab[];
  fetchStudentLabs: () => Promise<void>;

  // Instructor lab status
  labStatus: LabStatusResponse | null;
  fetchLabStatus: (className: string) => Promise<LabStatusResponse>;

  // Provisioning actions
  primeLabs: (className: string, count: number) => Promise<PrimeLabsResponse>;
  terminateLabs: (className: string) => Promise<void>;
  reduceInstances: (className: string, count: number) => Promise<ReduceLabsResponse>;

  // Status
  loading: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Hook for lab operations
 */
export function useLabs(): UseLabsResult {
  const [studentLabs, setStudentLabs] = useState<StudentLab[]>([]);
  const [labStatus, setLabStatus] = useState<LabStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch student's lab URLs
   */
  const fetchStudentLabs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet<{ success: boolean; data: StudentLab[] }>('/labs/my-labs');
      setStudentLabs(response.data);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to fetch labs';
      setError(message);
      console.error('Failed to fetch labs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch lab status for a class (instructor view)
   */
  const fetchLabStatus = useCallback(async (className: string): Promise<LabStatusResponse> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet<{ success: boolean; data: LabStatusResponse }>(
        `/labs/status/${encodeURIComponent(className)}`
      );
      setLabStatus(response.data);
      return response.data;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to fetch lab status';
      setError(message);
      console.error('Failed to fetch lab status:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Prime N lab instances for a class (instructor only)
   */
  const primeLabs = useCallback(
    async (className: string, count: number): Promise<PrimeLabsResponse> => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiPost<PrimeLabsResponse>(
          `/labs/prime/${encodeURIComponent(className)}`,
          { count }
        );
        // Update local state with new status
        if (response.data?.classStatus) {
          setLabStatus(response.data.classStatus);
        }
        return response;
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Failed to provision labs';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Terminate all labs for a class (instructor only)
   */
  const terminateLabs = useCallback(async (className: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await apiDelete(`/labs/terminate/${encodeURIComponent(className)}`);
      // Clear local status since all labs are being terminated
      setLabStatus(null);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to terminate labs';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reduce unassigned instances by count (instructor only)
   */
  const reduceInstances = useCallback(
    async (className: string, count: number): Promise<ReduceLabsResponse> => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiDelete<ReduceLabsResponse>(
          `/labs/reduce/${encodeURIComponent(className)}?count=${count}`
        );
        // Refresh status after reduction
        await fetchLabStatus(className);
        return response;
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Failed to reduce instances';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchLabStatus]
  );

  return {
    // Student labs
    studentLabs,
    fetchStudentLabs,

    // Instructor lab status
    labStatus,
    fetchLabStatus,

    // Provisioning actions
    primeLabs,
    terminateLabs,
    reduceInstances,

    // Status
    loading,
    error,
    clearError,
  };
}
